import { Controller } from "@hotwired/stimulus"
import confetti from 'canvas-confetti'

export default class extends Controller {
    static values = {
        effectType: String,
        storageKey: String
    }

    connect() {
        if (!this.shouldDisplay()) return

        this.triggerEffect()
        this.markAsShown()
    }

    shouldDisplay() {
        if (this.hasShownToday()) return false
        return true
    }

    triggerEffect() {
        const effectType = this.effectTypeValue || 'birthday'

        switch (effectType) {
            case 'birthday':
                this.launchConfetti()
                break
            case 'christmas':
                this.playChristmasAnimation()
                break
            case 'valentines':
                this.playValentinesAnimation()
                break
            default:
                console.warn(`Unknown effect type: ${effectType}`)
        }
    }

    launchConfetti() {
        setTimeout(() => {
            const duration = 5 * 1000
            const end = Date.now() + duration

            const frame = () => {
                confetti({
                    particleCount: 5,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0 }
                })
                confetti({
                    particleCount: 5,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1 }
                })

                if (Date.now() < end) {
                    requestAnimationFrame(frame)
                }
            }
            frame()
        }, 1000)
    }

    playChristmasAnimation() {
        if (!document.getElementById('snowflake-animation-style')) {
            const style = document.createElement('style');
            style.id = 'snowflake-animation-style';
            style.textContent = `
            .snowflake {
                position: fixed;
                top: -10px;
                color: #58c8ff;
                font-size: 24px;
                user-select: none;
                pointer-events: none;
                z-index: 9999;
                text-shadow: 0 0 8px #00BFFF, 0 0 15px #80E0FF, 0 0 25px #C0F8FF;
                animation: fall linear infinite;
                opacity: 0.95;
            }

            @keyframes fall {
                0% { transform: translateY(0) rotate(0deg); opacity: 1; }
                100% { transform: translateY(110vh) rotate(360deg); opacity: 0.8; }
            }
        `;
            document.head.appendChild(style);
        }

        const createSnowflake = () => {
            const s = document.createElement('div');
            s.classList.add('snowflake');
            s.textContent = '❄️';
            s.style.left = Math.random() * 100 + 'vw';
            s.style.fontSize = 16 + Math.random() * 24 + 'px';
            s.style.animationDuration = 5 + Math.random() * 7 + 's';
            s.style.opacity = 0.7 + Math.random() * 0.3;
            document.body.appendChild(s);

            setTimeout(() => s.remove(), 12000);
        };

        const initialCount = 70;
        for (let i = 0; i < initialCount; i++) {
            createSnowflake();
        }

        const snowflakeInterval = setInterval(createSnowflake, 300);

        setTimeout(() => clearInterval(snowflakeInterval), 8000);
    }

    playValentinesAnimation() {
        if (!document.getElementById('valentine-animation-style')) {
            const style = document.createElement('style');
            style.id = 'valentine-animation-style';
            style.textContent = `
            .valentine-heart {
                position: fixed;
                bottom: -50px;
                user-select: none;
                pointer-events: none;
                z-index: 9999;
                animation: floatUp linear infinite;
            }

            @keyframes floatUp {
                0% { 
                    transform: translateY(0) translateX(0) rotate(0deg) scale(1);
                    opacity: 0;
                }
                10% {
                    opacity: 1;
                }
                90% {
                    opacity: 0.9;
                }
                100% { 
                    transform: translateY(-110vh) translateX(var(--drift)) rotate(var(--rotation)) scale(0.5);
                    opacity: 0;
                }
            }

            @keyframes pulse {
                0%, 100% { transform: scale(1); }
                50% { transform: scale(1.1); }
            }
        `;
            document.head.appendChild(style);
        }

        const heartEmojis = ['❤️', '💕', '💖', '💗', '💓', '💝'];
        const colors = ['#FF1744', '#FF4081', '#F50057', '#E91E63', '#FF6E9F'];

        const createHeart = () => {
            const heart = document.createElement('div');
            heart.classList.add('valentine-heart');

            heart.textContent = heartEmojis[Math.floor(Math.random() * heartEmojis.length)];

            heart.style.left = Math.random() * 100 + 'vw';
            heart.style.fontSize = 20 + Math.random() * 30 + 'px';
            heart.style.animationDuration = 6 + Math.random() * 6 + 's';
            heart.style.opacity = 0.7 + Math.random() * 0.3;

            heart.style.setProperty('--drift', `${(Math.random() - 0.5) * 200}px`);
            heart.style.setProperty('--rotation', `${(Math.random() - 0.5) * 360}deg`);

            const color = colors[Math.floor(Math.random() * colors.length)];
            heart.style.filter = `drop-shadow(0 0 8px ${color}) brightness(1.2)`;

            document.body.appendChild(heart);

            setTimeout(() => heart.remove(), 13000);
        };

        const initialCount = 50;
        for (let i = 0; i < initialCount; i++) {
            setTimeout(() => createHeart(), i * 100);
        }

        const heartInterval = setInterval(createHeart, 400);

        setTimeout(() => clearInterval(heartInterval), 8000);
    }

    hasShownToday() {
        const lastShown = localStorage.getItem(this.storageKeyValue)
        if (!lastShown) return false

        const today = new Date().toDateString()
        return lastShown === today
    }

    markAsShown() {
        const today = new Date().toDateString()
        localStorage.setItem(this.storageKeyValue, today)
    }
}
